import React from 'react';
import './DataTable.less';

export type CellAlign = 'left' | 'center' | 'right';

export interface DataTableColumn<Row> {
  /** Unique key, also used to read the value from a row object. */
  key: keyof Row & string;
  /** Column header content. */
  header: React.ReactNode;
  align?: CellAlign;
  /** Prevent wrapping (useful for numeric cells). */
  nowrap?: boolean;
  /** Render this column's cell as a row header (th scope="row"). */
  isRowHeader?: boolean;
  /** Optional fixed/min width, e.g. "80px". */
  width?: string;
  /** Custom cell renderer; defaults to the raw row value. */
  render?: (row: Row, rowIndex: number) => React.ReactNode;
}

export interface DataTableProps<Row> {
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  /** Accessible caption (visually hidden). */
  caption?: string;
  className?: string;
  /** Allow horizontal scroll inside the table area (mobile, see T06). */
  scrollX?: boolean;
  /** Render the column header row. Set false for label/value tables that
   *  have no column titles (e.g. the schedule list). Defaults to true. */
  showHeader?: boolean;
}

/**
 * Compact semantic table used by both Summary and Trends.
 * Uses Figma table border / header colors and supports row-header cells,
 * per-column alignment, nowrap numbers and custom cell rendering (see T03).
 */
function DataTable<Row extends object>({
  columns,
  rows,
  caption,
  className,
  scrollX = false,
  showHeader = true,
}: DataTableProps<Row>): React.ReactElement {
  const cellClass = (col: DataTableColumn<Row>) =>
    [
      'data-table__cell',
      `data-table__cell--${col.align ?? 'left'}`,
      col.nowrap ? 'data-table__cell--nowrap' : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div
      className={[
        'data-table',
        scrollX ? 'data-table--scroll-x' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <table className="data-table__table">
        {caption && <caption className="data-table__caption">{caption}</caption>}
        {showHeader && (
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`${cellClass(col)} data-table__header`}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {columns.map((col) => {
                const content = col.render
                  ? col.render(row, rowIndex)
                  : (row[col.key] as React.ReactNode);

                if (col.isRowHeader) {
                  return (
                    <th key={col.key} scope="row" className={cellClass(col)}>
                      {content}
                    </th>
                  );
                }
                return (
                  <td key={col.key} className={cellClass(col)}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
